import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Upload, Loader2, Trash2, Inbox, MailCheck, MailX } from "lucide-react";
import { LogoMark, Wordmark } from "@/components/Logo";
import { api, getAdminToken, setAdminToken, resolveImg, formatApiErrorDetail } from "@/lib/api";
import { SITE } from "@/content";

const useAdminData = (token, refreshKey) => {
  const [photos, setPhotos] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (!token) return;
    api
      .get("/gallery")
      .then(({ data }) => setPhotos(data))
      .catch((e) => {
        if (e?.response?.status === 401) setAdminToken(null), window.location.reload();
        else toast.error(formatApiErrorDetail(e));
      });
    api
      .get("/bookings")
      .then(({ data }) => setBookings(data))
      .catch(() => {});
  }, [token, refreshKey]);

  return { photos, bookings };
};

const LoginView = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(), { response: { data, status: res.status } });
      onLogin(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#180407] flex items-center justify-center px-5 py-24">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="glass-card rounded-3xl w-full max-w-md p-9 sm:p-11"
      >
        <div className="flex flex-col items-center text-center gap-4 mb-9">
          <LogoMark size={52} />
          <Wordmark />
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mt-2">Admin Portal</p>
        </div>
        <label htmlFor="admin-password" className="block text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
          Password
        </label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          className="field-lux"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter the admin password"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-3 text-[13px] text-red-300">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-7 w-full rounded-full bg-[#D4AF37] py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#2C070B] hover:shadow-[0_0_36px_rgba(212,175,55,0.45)] transition-[box-shadow] duration-400 disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={15} className="animate-spin" />}
          {busy ? "Checking" : "Enter the Portal"}
        </button>
        <Link
          to="/"
          data-testid="admin-back-home"
          className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#A39791] hover:text-[#F3E5AB] transition-colors"
        >
          <ArrowLeft size={13} /> Back to the website
        </Link>
      </form>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(getAdminToken());
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(SITE.categories[0]);
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileRef = useRef(null);
  const { photos, bookings } = useAdminData(token, refreshKey);

  const logout = () => {
    setAdminToken(null);
    setToken(null);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!files || files.length === 0) {
      toast.error("Choose at least one photo to upload");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title);
        fd.append("category", category);
        await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added to the gallery`);
      setTitle("");
      setFiles(null);
      if (fileRef.current) fileRef.current.value = "";
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id);
      return;
    }
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Photo removed from the gallery");
      setConfirmId(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  if (!token) return <LoginView onLogin={(t) => (setAdminToken(t), setToken(t))} />;

  return (
    <div className="min-h-screen bg-[#180407] px-5 sm:px-8 py-28">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <LogoMark size={44} />
            <div>
              <p className="font-display text-lg tracking-[0.18em] text-[#F3E5AB]">GALLERY MANAGER</p>
              <p className="text-[12px] text-[#A39791] mt-1">
                {photos.length} photo{photos.length === 1 ? "" : "s"} live on the website
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors"
            >
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#2C070B] transition-colors flex items-center gap-2"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        <form
          data-testid="admin-upload-form"
          onSubmit={upload}
          className="glass-card rounded-2xl mt-10 p-7 grid md:grid-cols-12 gap-5 items-end"
        >
          <div className="md:col-span-3">
            <label htmlFor="up-title" className="block text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
              Photo Title
            </label>
            <input
              id="up-title"
              data-testid="admin-title-input"
              className="field-lux"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. The Grand Mandap"
            />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="up-cat" className="block text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
              Category
            </label>
            <select
              id="up-cat"
              data-testid="admin-category-select"
              className="field-lux"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {SITE.categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label htmlFor="up-file" className="block text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
              Choose Photos (multiple allowed)
            </label>
            <input
              id="up-file"
              ref={fileRef}
              data-testid="admin-photo-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              className="w-full text-[13px] text-[#A39791] file:mr-4 file:rounded-full file:border-0 file:bg-[#D4AF37]/15 file:px-5 file:py-2 file:text-[#F3E5AB] file:cursor-pointer hover:file:bg-[#D4AF37]/25 file:transition-colors"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-upload-button"
            disabled={uploading}
            className="md:col-span-2 rounded-full bg-[#D4AF37] py-3.5 px-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2C070B] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-[box-shadow] disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Adding" : "Add"}
          </button>
        </form>

        <h3 className="mt-14 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
          Gallery Photos
        </h3>
        <div data-testid="admin-photo-grid" className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-photo-item-${p.id}`}
              className="group relative overflow-hidden rounded-xl border border-[#D4AF37]/25 bg-[#2C070B]"
            >
              <img
                src={resolveImg(p)}
                alt={p.title || "Gallery photo"}
                className="w-full h-40 object-cover"
              />
              <div className="p-3">
                <p className="text-[13px] text-[#F3E5AB] truncate">{p.title}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#A39791] mt-0.5">{p.category}</p>
              </div>
              <button
                data-testid={`admin-delete-photo-button-${p.id}`}
                onClick={() => remove(p.id)}
                className={`absolute top-2.5 right-2.5 rounded-full p-2.5 backdrop-blur transition-colors duration-300 ${
                  confirmId === p.id
                    ? "bg-red-500 text-white"
                    : "bg-[#120204]/70 text-[#F3E5AB] opacity-0 group-hover:opacity-100 hover:bg-red-500/90"
                }`}
                aria-label={confirmId === p.id ? "Confirm delete" : "Delete photo"}
                title={confirmId === p.id ? "Click again to confirm" : "Delete photo"}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        {photos.length === 0 && (
          <p data-testid="admin-photo-empty" className="mt-6 text-sm text-[#A39791]">
            No photos yet — add your first ones with the form above.
          </p>
        )}

        <h3 className="mt-16 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
          Booking Enquiries ({bookings.length})
        </h3>
        <div data-testid="admin-bookings-list" className="mt-6 space-y-4 pb-16">
          {bookings.length === 0 && (
            <p data-testid="admin-bookings-empty" className="text-sm text-[#A39791] flex items-center gap-2">
              <Inbox size={15} /> No enquiries yet — they will appear here and in your email inbox.
            </p>
          )}
          {bookings.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-booking-item-${b.id}`}
              className="glass-card rounded-xl p-5 grid sm:grid-cols-12 gap-3 items-start"
            >
              <div className="sm:col-span-3">
                <p className="text-[15px] text-[#F3E5AB]">{b.name}</p>
                <p className="text-[12px] text-[#A39791] mt-1">{b.email}</p>
                <p className="text-[12px] text-[#A39791]">{b.phone}</p>
              </div>
              <div className="sm:col-span-4 text-[13px] text-[#FAF6F0]/85">
                <p>
                  {b.event_type}
                  {b.event_date ? ` · ${b.event_date}` : ""}
                  {b.guests ? ` · ${b.guests} guests` : ""}
                </p>
                {b.message && <p className="mt-1.5 text-[#A39791] font-light">"{b.message}"</p>}
              </div>
              <div className="sm:col-span-3 text-[11px] text-[#A39791]">
                {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
              </div>
              <div className="sm:col-span-2 sm:text-right">
                {b.email_sent ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300" data-testid={`booking-emailed-${b.id}`}>
                    <MailCheck size={13} /> Emailed
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-300" data-testid={`booking-not-emailed-${b.id}`}>
                    <MailX size={13} /> Email paused
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;
